import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { canViewPricingData } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { getCommunitiesByIds } from 'utils/helpers/communityMap';
import { formatDistanceFromKm, getDistanceUnitLabel } from 'utils/helpers/units';

import { GOOGLE_MAP_MARKER_INFO_OFFSET } from './constants';
import { ACTOR_TYPE } from '../../../constants';

interface ContainerProps {
  width?: number;
}

const Container = styled.div<ContainerProps>`
  position: absolute;
  top: ${GOOGLE_MAP_MARKER_INFO_OFFSET}px;
  visibility: hidden;
  opacity: 0;
  white-space: nowrap;
  font-size: 10px;
  font-family: var(--font-regular);
  padding: 6px 10px;
  color: #fff;
  border-radius: 10px;
  border: solid 1px #fff;
  background: linear-gradient(to left, var(--community-marker-purple-light), var(--community-marker-purple-dark));
  transition:
    opacity 400ms,
    visibility 400ms;
  z-index: 20;
`;

const ContainerWithAttrs = styled(Container).attrs<ContainerProps>((props) => ({
  style: {
    left: `-${(props.width ?? GOOGLE_MAP_MARKER_INFO_OFFSET) / 2}px`,
  },
}))``;

const StyledLink = styled(Link)`
  font-size: 12px;
  color: #fff;
  text-decoration: none;
`;

const AmountContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 4px;
  gap: 2px;
`;

const Amount = styled.span`
  font-size: 14px;
`;

const SubTitle = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 10px;
  opacity: 0.5;
`;

interface Props {
  data?: any;
}

const MapMarkerInfo = ({ data }: Props) => {
  const { communityMap } = useAppSelector((state) => state.global);
  const hasUnitSource = (value: any) => Boolean(value?.unit_system || value?.preferences?.unit_system);
  const { currentUser } = useAppSelector((state) => state.auth);
  const showPricingData = canViewPricingData(currentUser);
  const relatedCommunities = getCommunitiesByIds(communityMap, data?.communities).filter(
    (community: CommunityType) => !!community
  );
  const showDistance = relatedCommunities.length > 0;

  const distances = relatedCommunities.map((community: CommunityType) => {
    const distance = community?.distances?.[data?.id];
    const hasDistance = distance !== null && distance !== undefined;
    const unitSource = hasUnitSource(community) ? community : data;
    const distanceUnit = getDistanceUnitLabel(unitSource);
    return (
      <span key={community?.id}>
        {hasDistance
          ? `${formatDistanceFromKm(distance, unitSource, 2)} ${distanceUnit} from ${community?.comunity_name ?? 'Unknown'}`
          : 'Distance unavailable'}
      </span>
    );
  });

  const ref = useRef<HTMLDivElement>(null);
  const linkPrefix = data?.type === ACTOR_TYPE.COMPETITOR ? ACTOR_TYPE.COMPETITOR : ACTOR_TYPE.COMMUNITY;

  return (
    <ContainerWithAttrs ref={ref} className={'marker-info'} width={ref?.current?.clientWidth}>
      <StyledLink to={`/${linkPrefix}/${data?.id}`}>
        <span>{data?.comunity_name}</span>
        {showDistance && <SubTitle>{distances}</SubTitle>}
        {showPricingData && (
          <>
            <AmountContainer>
              <span>$</span>
              <Amount>{Math.round(data?.avg_base_rent).toLocaleString()}</Amount>
            </AmountContainer>
            <SubTitle>Market Rate Avg.</SubTitle>
          </>
        )}
      </StyledLink>
    </ContainerWithAttrs>
  );
};

export default MapMarkerInfo;
